import { useState } from 'react';
import type { ReactNode } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import { insidePaperFullWidthStyles, paperPaddingStyles } from '../Paper';
import { DownloadReportsPopupButton } from '../DownloadReportsPopupButton';
import type { DownloadReportsPopupButtonProps } from '../DownloadReportsPopupButton';
import MetaSelect from './MetaSelect';
import SelectedRespondentFilters from './SelectedRespondentFilters';
import { filterMetaGroups } from './filterMetaGroups';
import { RespondentFilterFooter } from './RespondentFilterFooter';
import { RespondentFilterSettings } from './RespondentFilterSettings';
import type { FilterMode } from './RespondentFilterSettings';
import { countRespondentsInMetaGroups } from './countRespondentsInMetaGroups';
import type {
    ResultFilter,
    RespondentFilterUsingMetaResultsQuery$data,
} from './__generated__/RespondentFilterUsingMetaResultsQuery.graphql';
import type { RespondentFilterUsingMetaGroups_questionnaire$key } from './__generated__/RespondentFilterUsingMetaGroups_questionnaire.graphql';

const FiltersRoot = ({ children }: { children: ReactNode }) => (
    <Box
        sx={(theme) => ({
            backgroundColor:
                theme.palette.mode === 'dark'
                    ? theme.bino.color.darkPurple
                    : theme.bino.color.purple,
            border: 'none',
            boxSizing: 'border-box',
            position: 'relative',
            ...insidePaperFullWidthStyles(theme),
        })}
    >
        <Box
            sx={(theme) => ({
                ...paperPaddingStyles(theme),
            })}
        >
            {children}
        </Box>
    </Box>
);

function makeMetaResults(
    metaGroups: RespondentFilterUsingMetaGroupsProps['metaGroups'],
): RespondentFilterUsingMetaResultsQuery$data['metaResults'] {
    const metaResults = [] as Array<
        RespondentFilterUsingMetaResultsQuery$data['metaResults'][0]
    >;
    const metaMap = {} as Record<string, number>;
    metaGroups.forEach(({ answerCount, metaQuestionResponses }) => {
        metaQuestionResponses.forEach(
            ({ metaQuestionId: metaKey, responseOptionValue }) => {
                const metaValue = responseOptionValue.toString();
                const key = metaKey + '=' + metaValue;
                const index = metaMap[key];
                if (index !== undefined) {
                    metaResults[index] = {
                        ...metaResults[index],
                        responseCount:
                            metaResults[index].responseCount + answerCount,
                    };
                } else {
                    metaMap[key] = metaResults.length;
                    metaResults.push({
                        metaKey,
                        metaValue,
                        responseCount: answerCount,
                    });
                }
            },
        );
    });
    return metaResults;
}

export interface RespondentFilterUsingMetaGroupsProps {
    visible?: boolean;

    filters: Array<ResultFilter>;
    filterMode: FilterMode;

    questionnaire: RespondentFilterUsingMetaGroups_questionnaire$key;
    /**
     * If present, the filter will display a "download filtered report" button
     */
    DownloadReportButtonProps?: DownloadReportsPopupButtonProps;

    // FIXME We really just need the project count it seems
    projects: Array<never>;
    metaGroups: ReadonlyArray<{
        readonly answerCount: number;
        readonly metaQuestionResponses: ReadonlyArray<{
            readonly metaQuestionId: string;
            readonly responseOptionValue: number;
        }>;
    }>;

    onChange(filters: Array<ResultFilter>, filterMode: FilterMode): void;
}

const RespondentFilterUsingMetaGroups = (
    props: RespondentFilterUsingMetaGroupsProps,
) => {
    const [filters, setFilters] = useState(props.filters);
    const [filterMode, setFilterMode] = useState(props.filterMode);
    const [visible, setVisible] = useState(!!props.visible); // Not sure why we have this controllable
    const questionnaire = useFragment(
        fragments.questionnaire,
        props.questionnaire,
    );
    const { projects, metaGroups, onChange, DownloadReportButtonProps } = props;

    const clearFilter = () => {
        setVisible(props.visible || false);
        setFilters([]);
        // todo Should we reset to LogicalAnd filter as well?
        onChange([], filterMode);
    };

    const applyFilter = () => {
        setVisible(props.visible || false);
        onChange(filters, filterMode);
    };

    const handleSelectMeta = (metaKey: string, metaValues: string[]) => {
        const newFilters = filters.filter(
            (filter) => filter.metaKey !== metaKey,
        );
        if (metaValues.length) {
            newFilters.push({ metaKey, metaValues });
        }
        setFilters(newFilters);
    };

    const filteredMetaGroups = filterMetaGroups(
        metaGroups,
        filters,
        filterMode,
    );

    const filteredRespondentCount =
        countRespondentsInMetaGroups(filteredMetaGroups);

    const metaList = questionnaire.questions
        .filter(({ isMeta, isHidden }) => isMeta && !isHidden)
        .filter(({ tags }) => !tags.includes('choose-survey'))
        .map((question) => ({
            label: question.shortForm,
            options: questionnaire.responseScales
                .find(
                    ({ originalId }) => originalId === question.responseScaleId,
                )
                .responseOptions.map(({ label, value }) => ({
                    label: label,
                    value: value.toString(), // graphql wants strings for metaValue
                })),
            metaKey: question.originalId,
            metaValues:
                filters
                    .filter(({ metaKey }) => metaKey === question.originalId)
                    .map(({ metaValues }) => metaValues)
                    .pop() || [],
        }));

    if (!visible) {
        return (
            <Stack gap={2}>
                <Stack direction="row" gap={2}>
                    <Button variant="outlined" onClick={() => setVisible(true)}>
                        <FormattedMessage
                            defaultMessage="Filter respondents"
                            description="Filter Respondents Button"
                        />
                    </Button>
                    {filters.length > 0 && DownloadReportButtonProps && (
                        <DownloadReportsPopupButton
                            buttonVariant="text"
                            {...DownloadReportButtonProps}
                            filter={{
                                filters,
                                filterMode,
                                description: (
                                    <SelectedRespondentFilters
                                        small
                                        metaList={metaList}
                                        metaResults={makeMetaResults(
                                            filteredMetaGroups,
                                        )}
                                        filterMode={filterMode}
                                    />
                                ),
                            }}
                        >
                            <FormattedMessage
                                defaultMessage="Download filtered report"
                                description="Download filtered report button"
                            />
                        </DownloadReportsPopupButton>
                    )}
                </Stack>
                <SelectedRespondentFilters
                    metaList={metaList}
                    metaResults={makeMetaResults(filteredMetaGroups)}
                    filterMode={filterMode}
                />
                <Box fontSize={14}>
                    <FormattedMessage
                        defaultMessage="Number of respondents: {respondentCount}"
                        description="Filter Respondents Button"
                        values={{
                            respondentCount: filteredRespondentCount,
                        }}
                    />
                </Box>
            </Stack>
        );
    }

    return (
        <FiltersRoot>
            <RespondentFilterSettings
                filterMode={filterMode}
                onChangeFilterMode={setFilterMode}
            />
            {metaList.map((meta) => {
                const allFiltersExceptForThisMetaKey =
                    filterMode === 'LogicalOr'
                        ? []
                        : filters.filter(
                              (filter) => filter.metaKey !== meta.metaKey,
                          );
                const filteredMetaGroups = filterMetaGroups(
                    metaGroups,
                    allFiltersExceptForThisMetaKey,
                    filterMode,
                );
                return (
                    <MetaSelect
                        key={meta.metaKey}
                        useAutocomplete={true}
                        useMetaResultsLoader={false}
                        onChange={handleSelectMeta}
                        questionnaireId={questionnaire.originalId}
                        filters={filters}
                        metaResults={makeMetaResults(filteredMetaGroups)}
                        {...meta}
                    />
                );
            })}
            <RespondentFilterFooter
                respondentCount={filteredRespondentCount}
                projectCount={projects?.length}
                onFilter={applyFilter}
                onClear={clearFilter}
            />
        </FiltersRoot>
    );
};

const fragments = {
    questionnaire: graphql`
        fragment RespondentFilterUsingMetaGroups_questionnaire on QuestionnaireInterface {
            originalId
            questions {
                originalId
                isMeta
                shortForm
                responseScaleId: originalResponseScaleId
                tags
                isHidden
            }
            responseScales {
                originalId
                responseOptions {
                    value
                    label
                }
            }
        }
    `,
};

export default RespondentFilterUsingMetaGroups;
